<template>
	<nuxt-picture
		v-if="source"
		:src="source"
		:alt="alt ?? ''"
		provider="storyblok"
		loading="lazy"
		v-bind="sbImageDimensions(source)"
		sizes="xs:100vw sm:100vw md:555px lg:725px xl:896px 2xl:1067px"
		:img-attrs="{title}"
	/>
</template>

<script setup lang="ts">
/*
 * Replacement for SB's built-in rich text image renderer. Main difference is that we use <nuxt-picture> so that the images are served in an optimized format and also resized.
 *
 * The `sizes` breakpoints are the rendered widths of the prose container, so the browser never downloads a larger source than it can display.
 */
import type {SbVueRichTextProps} from "@storyblok/vue";
import sbImageDimensions from "@pkg/shared/utils/sb-image-dimensions";

// eslint-disable-next-line vue/prop-name-casing -- Lint issues come from the library
const properties = defineProps<SbVueRichTextProps["image"]>();

// `filename` isn't part of the rich text image node, but rich text documents built by hand
// elsewhere in our content sometimes carry the raw asset shape instead
const source = computed(
	() => getStringAttribute("src") ?? getStringAttribute("filename"),
);

const alt = computed(() => getStringAttribute("alt"));
const title = computed(() => getStringAttribute("title"));

function getStringAttribute(key: string) {
	const value = properties.attrs?.[key];

	return typeof value === "string" && value.length > 0 ? value : undefined;
}
</script>
